import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const openDialogs = [];
let originalSiblings = [];
let originalOverflow = "";
const updateBackground = () => {
  const top = openDialogs[openDialogs.length - 1];
  if (top) {
    [...document.body.children].forEach((node) => {
      node.inert = node !== top;
      if (node === top) node.removeAttribute("aria-hidden"); else node.setAttribute("aria-hidden", "true");
    });
  } else {
    originalSiblings.forEach(([node, inert, hidden]) => {
      node.inert = inert;
      if (hidden === null) node.removeAttribute("aria-hidden"); else node.setAttribute("aria-hidden", hidden);
    });
    document.body.style.overflow = originalOverflow;
    originalSiblings = [];
  }
};
const focusable = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, iframe, [tabindex="0"]';

export default function Dialog({ title, children, onClose, footer, busy = false, size = "max-w-2xl", closeLabel = "Fermer la fenêtre", returnFocusRef }) {
  const titleId = useId();
  const panel = useRef(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);
  const [host] = useState(() => document.createElement("div"));
  closeRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    const previousFocus = returnFocusRef?.current || document.activeElement;
    if (!openDialogs.length) {
      originalOverflow = document.body.style.overflow;
      originalSiblings = [...document.body.children].map((node) => [node, node.inert, node.getAttribute("aria-hidden")]);
    }
    document.body.appendChild(host);
    document.body.style.overflow = "hidden";
    openDialogs.push(host);
    updateBackground();
    (panel.current?.querySelector("[data-autofocus]") || panel.current?.querySelector("button, input, select, textarea"))?.focus();
    const handleKey = (event) => {
      if (openDialogs[openDialogs.length - 1] !== host) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (!busyRef.current) closeRef.current?.();
      }
      if (event.key === "Tab") {
        const elements = [...(panel.current?.querySelectorAll(focusable) || [])]
          .filter((node) => !node.closest("[hidden]") && !node.closest("details:not([open]) :not(summary)"));
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (!first) { event.preventDefault(); panel.current?.focus(); return; }
        if (!panel.current?.contains(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      openDialogs.splice(openDialogs.indexOf(host), 1);
      host.remove();
      updateBackground();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [host, returnFocusRef]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 p-3 sm:p-6"
      onClick={(event) => { if (event.target === event.currentTarget && !busy) onClose?.(); }}>
      <section ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        className={`flex max-h-[90vh] w-full flex-col rounded-xl bg-white shadow-xl ${size}`}>
        <header className="flex items-center justify-between gap-4 border-b border-gray-200 p-4 sm:px-6">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h2>
          <button type="button" aria-label={closeLabel} disabled={busy} onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </header>
        <div className="min-h-0 overflow-y-auto p-4 sm:p-6">{children}</div>
        {footer && <footer className="flex flex-col-reverse gap-3 border-t border-gray-200 p-4 sm:flex-row sm:justify-end sm:px-6">{footer}</footer>}
      </section>
    </div>, host
  );
}
